import { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios'
import { Auth } from '@aws-amplify/auth'
import { AuthService } from '../auth.service'
import { store } from '../../redux/store'
import { logout } from '../../redux/authenticator'

let isRefreshing = false
let refreshSubscribers: ((token: string) => void)[] = []

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb)
}

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach(cb => cb(token))
  refreshSubscribers = []
}

export const setupAuthInterceptor = (axiosInstance: AxiosInstance) => {
  // Request interceptor to add auth token
  axiosInstance.interceptors.request.use(
    async (config: AxiosRequestConfig) => {
      try {
        const session = await AuthService.getCurrentSession()
        if (session) {
          const accessToken = session.getAccessToken().getJwtToken()
          if (config.headers) {
            config.headers.Authorization = `Bearer ${accessToken}`
          }
        }
      } catch (error) {
        // No session available, proceed without token
      }
      return config
    },
    error => {
      return Promise.reject(error)
    }
  )

  // Response interceptor to handle token refresh
  axiosInstance.interceptors.response.use(
    response => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }

      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise(resolve => {
            subscribeTokenRefresh((token: string) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`
              }
              resolve(axiosInstance(originalRequest))
            })
          })
        }

        originalRequest._retry = true
        isRefreshing = true

        try {
          const newSession = await AuthService.refreshSession() as any
          const newToken = newSession.getAccessToken().getJwtToken()
          
          onTokenRefreshed(newToken)
          isRefreshing = false

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`
          }
          return axiosInstance(originalRequest)
        } catch (refreshError) {
          isRefreshing = false
          refreshSubscribers = []
          
          // Refresh failed, logout user
          store.dispatch(logout())
          window.location.href = '/login'
          
          return Promise.reject(refreshError)
        }
      }

      return Promise.reject(error)
    }
  )
}

// Helper to setup interceptors for all axios instances
export const setupAllInterceptors = (instances: AxiosInstance[]) => {
  instances.forEach(instance => setupAuthInterceptor(instance))
}